import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as reflectionApi from '../../services/reflection-api.js';

@customElement('cp-quick-note-modal')
export class CpQuickNoteModal extends LitElement {
  createRenderRoot() { return this; }

  @state() private _open = false;
  @state() private _textValue = '';
  @state() private _tagValue = '';
  @state() private _submitting = false;
  @state() private _submitError = '';
  @state() private _showSuccess = false;

  open(): void {
    this._resetForm();
    this._open = true;
    this.updateComplete.then(() => {
      const textarea = this.querySelector<HTMLTextAreaElement>('.cp-quick-note-modal__textarea');
      textarea?.focus();
    });
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onGlobalKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onGlobalKeyDown);
  }

  private _close(): void {
    this._open = false;
  }

  private _resetForm(): void {
    this._textValue = '';
    this._tagValue = '';
    this._submitting = false;
    this._submitError = '';
    this._showSuccess = false;
  }

  private _onTextInput(e: Event): void {
    this._textValue = (e.target as HTMLTextAreaElement).value;
    this._submitError = '';
  }

  private _onTagInput(e: Event): void {
    this._tagValue = (e.target as HTMLInputElement).value;
  }

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-quick-note-modal__overlay')) {
      this._close();
    }
  }

  private _onGlobalKeyDown = (e: KeyboardEvent): void => {
    // Cmd+Option+Shift+R (Mac) or Ctrl+Alt+Shift+R (Win/Linux) to open
    if ((e.ctrlKey || e.metaKey) && e.altKey && e.shiftKey && e.key === 'R') {
      e.preventDefault();
      if (!this._open) {
        this.open();
      }
      return;
    }

    // Escape to close
    if (e.key === 'Escape' && this._open) {
      this._close();
    }
  };

  private _onDialogKeyDown(e: KeyboardEvent): void {
    // Cmd/Ctrl+Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !this._submitting) {
      e.preventDefault();
      void this._onSubmit();
    }
  }

  private async _onSubmit(): Promise<void> {
    const text = this._textValue.trim();

    if (!text) {
      this._submitError = 'Text ist erforderlich';
      return;
    }

    if (this._submitting) return;

    this._submitting = true;
    this._submitError = '';

    try {
      const tag = this._tagValue.trim() || undefined;
      await reflectionApi.createNote(text, tag);
      this._close();
      this._showSuccessMessage();
    } catch (e) {
      this._submitError = e instanceof Error ? e.message : 'Note konnte nicht gespeichert werden';
    } finally {
      this._submitting = false;
    }
  }

  private _showSuccessMessage(): void {
    this._showSuccess = true;
    setTimeout(() => {
      this._showSuccess = false;
    }, 2000);
  }

  render() {
    return html`
      ${this._open ? html`
        <div class="cp-quick-note-modal__overlay" @click=${this._onBackdropClick}>
          <div class="cp-quick-note-modal__dialog" @keydown=${this._onDialogKeyDown}>
            <div class="cp-quick-note-modal__header">
              <h2 class="cp-quick-note-modal__title">Quick Note</h2>
              <span class="cp-quick-note-modal__shortcut-hint">Cmd/Ctrl+Enter = Speichern</span>
              <button class="cp-quick-note-modal__close" @click=${this._close}>&times;</button>
            </div>

            <div class="cp-quick-note-modal__body">
              <div class="cp-quick-note-modal__field">
                <textarea
                  class="cp-quick-note-modal__textarea"
                  placeholder="Was ist passiert? Was geht dir durch den Kopf?"
                  .value=${this._textValue}
                  @input=${this._onTextInput}
                  rows="3"
                ></textarea>
              </div>

              <div class="cp-quick-note-modal__field cp-quick-note-modal__field--tag">
                <label class="cp-quick-note-modal__label">Tag (optional)</label>
                <input
                  class="cp-quick-note-modal__input"
                  type="text"
                  placeholder="z.B. arbeit, gesundheit, idee..."
                  .value=${this._tagValue}
                  @input=${this._onTagInput}
                />
              </div>

              ${this._submitError ? html`
                <div class="cp-quick-note-modal__error">${this._submitError}</div>
              ` : nothing}
            </div>

            <div class="cp-quick-note-modal__footer">
              <button class="cp-quick-note-modal__btn cp-quick-note-modal__btn--cancel" @click=${this._close}>Abbrechen</button>
              <button
                class="cp-quick-note-modal__btn cp-quick-note-modal__btn--submit"
                ?disabled=${!this._textValue.trim() || this._submitting}
                @click=${this._onSubmit}
              >${this._submitting ? 'Wird gespeichert...' : 'Speichern'}</button>
            </div>
          </div>
        </div>
      ` : nothing}

      ${this._showSuccess ? html`
        <div class="cp-quick-note-modal__toast">Note gespeichert</div>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-quick-note-modal': CpQuickNoteModal;
  }
}
